import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import * as React from "react";
import Document from "documents/document";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import Individual from "marketplace/individuals/individual";
import { clearPermissionFilters } from "permissions/_views/tables/_utilities/utils";
import { FilterButtons } from "permissions/_views/tables/permissions-table-styles";
import { TableRow, CategoryLabel, CATEGORY_OPTIONS } from "permissions/_views/types/permission-types";
import { type MRT_TableInstance as TableInstance } from "material-react-table";
import BarebonesWorkAgreementInfoAPIResponse from "work/work-agreements/_api/responses/barebones-work-agreement-api-response";

const CategorySelect = styled(Select)(({ theme }) => ({
  "& .MuiSelect-select": {
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
  },
}));

export type PermissionsToolbarProps = {
  // Table instance
  table: TableInstance<TableRow>;
  // Current querystring, e.g. location.search
  search: string;
  // Router navigate function: navigate({ search: string }, { replace?: boolean })
  navigate: (to: { search: string }, opts?: { replace?: boolean }) => void;

  // UI state provided by parent
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  isFilterPanelOpen: boolean;
  onToggleFilterPanel: () => void;
  activeCategory: CategoryLabel | "";
  setActiveCategory: (val: CategoryLabel | "") => void;
  hasParent: boolean;

  // Lookup maps for chip labels
  individualByUserId: Map<string, Individual>;
  documentById: Map<string, Document | UnavailableDocument>;
  proposalById: Map<string, BarebonesProposalResponse>;
  workAgreementById: Map<string, BarebonesWorkAgreementInfoAPIResponse>;

  // Callbacks
  onBackButtonClicked: () => void;
  onClearFiltersClicked?: () => void;
};

export default function PermissionsToolbar(props: Readonly<PermissionsToolbarProps>) {
  const {
    table,
    search,
    navigate,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    isFilterPanelOpen,
    onToggleFilterPanel,
    activeCategory,
    setActiveCategory,
    hasParent,
    individualByUserId,
    documentById,
    proposalById,
    workAgreementById,
    onBackButtonClicked,
    onClearFiltersClicked,
  } = props;

  const theme = useTheme();
  const qs = React.useMemo(() => new URLSearchParams(search), [search]);

  // Active filters (read-only, from URL)
  const activeCategories = qs.getAll("categories");
  const activeGranteeUserIds = qs.getAll("granteeUserIds");
  const activeAccessType = qs.get("accessType");
  const activeDocumentIds = qs.getAll("documentIds");
  const activeProposalIds = qs.getAll("proposalIds");
  const activeWorkAgreementIds = qs.getAll("workAgreementIds");
  const hasActiveFilters: boolean =
    activeCategories.length > 0 ||
    activeGranteeUserIds.length > 0 ||
    !!activeAccessType ||
    activeDocumentIds.length > 0 ||
    activeProposalIds.length > 0 ||
    activeWorkAgreementIds.length > 0;

  const getHasAdditionalFilters = (params: URLSearchParams) =>
    params.getAll("categories").length > 0 ||
    params.getAll("granteeUserIds").length > 0 ||
    !!params.get("accessType") ||
    params.getAll("documentIds").length > 0 ||
    params.getAll("proposalIds").length > 0 ||
    params.getAll("workAgreementIds").length > 0;

  const handleSetParam = React.useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(search);
      if (value == null || value === "") params.delete(key);
      else params.set(key, value);
      // If no extra filters left, drop hasParent
      if (!getHasAdditionalFilters(params)) params.delete("hasParent");
      navigate({ search: params.toString() });
    },
    [props, search]
  );

  const handleAppendList = React.useCallback(
    (key: string, values: string[]) => {
      const params = new URLSearchParams(search);
      params.delete(key);
      for (const v of values) params.append(key, v);
      if (!getHasAdditionalFilters(params)) params.delete("hasParent");
      navigate({ search: params.toString() });
    },
    [props, search]
  );

  const onClearFilters = React.useCallback(() => {
    if (onClearFiltersClicked) return onClearFiltersClicked();
    navigate({ search: clearPermissionFilters(search) });
  }, [props, search]);

  const handleBackButtonClicked = () => {
    if (isSingleGranteeView || isManageTemplatesView || hasParent) {
      navigate({ search: "" });
    }
    table.setCreatingRow(null);
    table.setEditingRow(null);
    onBackButtonClicked();
  };

  return (
    <FilterButtons>
      {/* Back from single-grantee or manage/create template view */}
      {(isSingleGranteeView || isCreateTemplateView || isManageTemplatesView || hasParent) && (
        <Chip
          color="primary"
          icon={<ArrowBackIcon />}
          label="Back"
          variant="filled"
          onClick={handleBackButtonClicked}
        />
      )}

      {/* Category select */}
      <FormControl size="small" sx={{ minWidth: 150, ml: 2, mr: 1 }}>
        <CategorySelect
          displayEmpty
          size="small"
          variant="outlined"
          value={activeCategory}
          onChange={(e) => {
            const newValue = e.target.value as CategoryLabel | "";
            setActiveCategory(newValue);
            handleSetParam("category", newValue);
          }}
          renderValue={(value) => {
            if (!value) {
              return <Box sx={{ px: 1, py: 0.5, borderRadius: 1 }}>All Categories</Box>;
            }
            const opt = CATEGORY_OPTIONS.find((o) => o.label === value)!;
            const fg = theme.palette.getContrastText(opt.color);
            return <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: opt.color, color: fg }}>{opt.label}</Box>;
          }}
        >
          <MenuItem value="">All Categories</MenuItem>
          <Divider />
          {CATEGORY_OPTIONS.map((opt) => {
            const fg = theme.palette.getContrastText(opt.color);
            const bg = opt.color;
            return (
              <MenuItem
                key={opt.label}
                value={opt.label}
                sx={{ bgcolor: "transparent", "&:hover": { bgcolor: theme.palette.action.hover } }}
              >
                <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: bg, color: fg }}>{opt.label}</Box>
              </MenuItem>
            );
          })}
        </CategorySelect>
      </FormControl>

      {/* Toggle panel */}
      <Chip
        icon={<FilterListIcon />}
        label="Additional Permissions"
        color="primary"
        variant={isFilterPanelOpen ? "filled" : "outlined"}
        onClick={onToggleFilterPanel}
      />

      {/* Active chips */}
      {activeCategories.map((category) => (
        <Chip
          key={`cat:${category}`}
          label={`Category: ${category}`}
          color="primary"
          variant="outlined"
          onDelete={() => {
            const next = activeCategories.filter((c) => c !== category);
            handleAppendList("categories", next);
          }}
        />
      ))}

      {activeGranteeUserIds.map((userId) => (
        <Chip
          key={`usr:${userId}`}
          label={`Grantee: ${individualByUserId.get(userId)?.fullName ?? "Unknown User"}`}
          color="primary"
          variant="outlined"
          onDelete={() => {
            const next = activeGranteeUserIds.filter((id) => id !== userId);
            handleAppendList("granteeUserIds", next);
          }}
        />
      ))}

      {activeAccessType && (
        <Chip
          label={`Type: ${activeAccessType}`}
          color="primary"
          variant="outlined"
          onDelete={() => handleSetParam("accessType", null)}
        />
      )}

      {activeDocumentIds.map((documentId) => {
        const doc = documentById.get(documentId);
        let label: string;
        if (doc && "name" in doc && typeof doc.name?.value === "string") {
          label = `Document: ${doc.name.toString()}`;
        } else if (doc && !("name" in doc)) {
          label = `Document: Unavailable`;
        } else {
          label = "Document: Unknown";
        }
        return (
          <Chip
            key={`doc:${documentId}`}
            label={label}
            color="primary"
            variant="outlined"
            onDelete={() => {
              const next = activeDocumentIds.filter((id) => id !== documentId);
              handleAppendList("documentIds", next);
            }}
          />
        );
      })}

      {activeProposalIds.map((proposalId) => (
        <Chip
          key={`prop:${proposalId}`}
          label={`Proposal: ${proposalById.get(proposalId)?.name ?? "Unknown Proposal"}`}
          color="primary"
          variant="outlined"
          onDelete={() => {
            const next = activeProposalIds.filter((id) => id !== proposalId);
            handleAppendList("proposalIds", next);
          }}
        />
      ))}

      {activeWorkAgreementIds.map((workId) => (
        <Chip
          key={`work:${workId}`}
          label={`Work Agreement: ${workAgreementById.get(workId)?.name ?? "Unknown Work Agreement"}`}
          color="primary"
          variant="outlined"
          onDelete={() => {
            const next = activeWorkAgreementIds.filter((id) => id !== workId);
            handleAppendList("workAgreementIds", next);
          }}
        />
      ))}

      {/* Clear all */}
      {hasActiveFilters && (
        <Chip
          icon={<ClearIcon />}
          label="Clear Filters"
          color="primary"
          variant="outlined"
          onClick={onClearFilters}
        />
      )}
    </FilterButtons>
  );
}
